import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import ErrorOutline from '@mui/icons-material/ErrorOutline';

import { PriorityTagsCell } from '@app/application/components/item/PriorityTagsCell';
import { Tooltip } from '@app/ui/tooltip';

import { ResultsViewGc } from '../utils/build-results-view';
import { formatCalendarDate } from '../utils/format-calendar-date';
import { PairRow } from './PairRow';
import { buildPairKey } from '../session';
import { SentCue } from './SentCue';

interface GcSectionProps {
  readonly gc: ResultsViewGc;
  readonly sentPairKeys: string[];
  readonly onEmailPair: (ipId: number) => void;
}

// Expansion state lives HERE, not at the screen root: a toggle then re-renders one section rather
// than all N, and no screen-level id set exists to leak or mis-derive (PLAN §2.1c).
export const GcSection = ({ gc, sentPairKeys, onEmailPair }: GcSectionProps) => {
  const { t } = useTranslation('common');

  const [expandedIpIds, setExpandedIpIds] = useState<number[]>([]);

  const allExpanded = gc.pairs.length > 0 && expandedIpIds.length === gc.pairs.length;

  const isPairSent = (ipId: number) => sentPairKeys.includes(buildPairKey(gc.id, ipId));
  const anyPairSent = gc.pairs.some(pair => isPairSent(pair.ipId));

  const toggleIp = (ipId: number) => {
    setExpandedIpIds(current => (current.includes(ipId) ? current.filter(id => id !== ipId) : [...current, ipId]));
  };

  return (
    <Box sx={{ border: 1, borderColor: 'grey.500', borderRadius: 5, overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          py: 2.5,
          px: 3,
          // Design #FCF7FB; nearest token, 03's precedent (EVIDENCE deviation 1).
          bgcolor: 'primaryLight.light',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="h4" component="h4">
              {gc.displayName}
            </Typography>
            {gc.hasDataGap && (
              <Tooltip title={t('matching.results.entityDataGapTooltip')} color="inherit" iconSize="inherit">
                <ErrorOutline sx={{ fontSize: 22, display: 'inline-flex', color: 'warning.main' }} />
              </Tooltip>
            )}
            {/* AC-7 (reinstated 2026-09-08): colour glyphs → translated word, unmapped tags → raw name, nothing hidden.
                Tech-lead ruling 2026-09-11: on THIS screen only, an absent/empty tag list renders nothing —
                other `PriorityTagsCell` consumers keep its own "-" fallback, so the guard lives at this call site. */}
            {!!gc.priorityTags?.length && <PriorityTagsCell tags={gc.priorityTags} />}
            {anyPairSent && <SentCue title={t('matching.results.sendCueGc')} />}
          </Box>

          <Typography variant="body1" color="text.secondary">
            {gc.surroAvailableInDatabase
              ? t('matching.results.availableSince', {
                  date: formatCalendarDate(gc.surroAvailableInDatabase),
                })
              : t('matching.results.noDate')}
          </Typography>
        </Box>

        {gc.pairs.length > 0 && (
          <Typography
            component="button"
            type="button"
            variant="h5"
            aria-label={`${t(allExpanded ? 'matching.results.collapseAll' : 'matching.results.expandAll')} — ${
              gc.displayName
            }`}
            onClick={() => setExpandedIpIds(allExpanded ? [] : gc.pairs.map(pair => pair.ipId))}
            sx={{
              p: 0,
              border: 'none',
              bgcolor: 'transparent',
              color: 'primary.main',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              '&:hover': { color: 'primary.dark' },
            }}
          >
            {t(allExpanded ? 'matching.results.collapseAll' : 'matching.results.expandAll')}
          </Typography>
        )}
      </Box>

      {gc.pairs.length ? (
        gc.pairs.map(pair => (
          <PairRow
            key={pair.ipId}
            pair={pair}
            open={expandedIpIds.includes(pair.ipId)}
            onToggle={() => toggleIp(pair.ipId)}
            sent={isPairSent(pair.ipId)}
            onEmail={() => onEmailPair(pair.ipId)}
          />
        ))
      ) : (
        // G-6 — a zero-pair GC keeps its full header and gets one line. Never hidden (AC-1).
        <Typography variant="body1" color="text.secondary" sx={{ borderTop: 1, borderColor: 'grey.500', py: 2, px: 3 }}>
          {t('matching.results.noCandidates')}
        </Typography>
      )}
    </Box>
  );
};
